import { clsx, type ClassValue } from "clsx";
import { twMerge } from "tailwind-merge";

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

/**
 * Compact count for space-constrained chips (e.g. sidebar badges): exact below
 * 1,000, then 1,500 -> "1.5k", 2,000,000 -> "2m". Keep exact numbers
 * (page ranges, totals, admin tables) out of this — it intentionally loses
 * precision.
 */
export function fmtCount(n: number): string {
  if (n >= 1_000_000_000) return `${(n / 1_000_000_000).toFixed(n % 1_000_000_000 === 0 ? 0 : 1)}b`;
  if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(n % 1_000_000 === 0 ? 0 : 1)}m`;
  if (n >= 1_000) return `${(n / 1_000).toFixed(n % 1_000 === 0 ? 0 : 1)}k`;
  return String(n);
}

/**
 * Formats a phone number for display as xxx-xxx-xxxx.
 *
 * Strips all non-digit characters, drops a leading US country code "1" when
 * the result is 11 digits, then renders a 10-digit number as xxx-xxx-xxxx.
 * Anything that isn't a clean 10/11-digit number is returned unchanged so we
 * never mangle international or partial values. Empty/null input returns "".
 */
export function formatPhone(phone: string | null | undefined): string {
  if (!phone) return "";
  const digits = phone.replace(/\D/g, "");
  const local =
    digits.length === 11 && digits.startsWith("1") ? digits.slice(1) : digits;
  if (local.length !== 10) return phone;
  return `${local.slice(0, 3)}-${local.slice(3, 6)}-${local.slice(6)}`;
}

/**
 * Converts a string to Title Case — first letter of each word capitalized, the
 * rest lowercased. Handles separators like spaces, hyphens and apostrophes
 * (e.g. "tarun upadhyay" → "Tarun Upadhyay", "o'brien" → "O'Brien").
 */
export function titleCase(value: string | null | undefined): string {
  if (!value) return "";
  return value
    .toLowerCase()
    .replace(/(^|[\s'’-])(\p{L})/gu, (_, sep, ch) => sep + ch.toUpperCase());
}

/**
 * Formats a date as MM/DD/YYYY (e.g. 06/20/2026) — the platform-wide date format.
 * Returns "—" for empty/invalid input to match the platform's date placeholders.
 */
export function formatDate(value: string | null | undefined): string {
  if (!value) return "—";
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return "—";
  return d.toLocaleDateString("en-US", {
    month: "2-digit",
    day: "2-digit",
    year: "numeric",
  });
}

/**
 * Same date as formatDate, plus the wall-clock time — for columns where "when
 * exactly" matters (last login), not just "which day". Rendered in the
 * viewer's local timezone, since the stored values are timezone-aware UTC.
 */
export function formatDateTime(value: string | null | undefined): string {
  if (!value) return "—";
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return "—";
  return d.toLocaleString("en-US", {
    month: "2-digit",
    day: "2-digit",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

/**
 * Returns the "hot" badge type for a listing based on its publish time.
 *
 * - "this-hour"  → published today AND current time is within 4 hours of publish
 * - "new-today"  → published on the publish day OR the full next calendar day
 *                  e.g. published 25-05-2026 → badge shows all of 25-05 and all of 26-05
 * - null         → published 2+ days ago, or not yet published (no badge)
 */
export function getListingHotBadge(
  published_at: string | null | undefined
): "this-hour" | "new-today" | null {
  if (!published_at) return null;

  const created = new Date(published_at);
  const now = new Date();

  // Strip both dates down to midnight (local time) for pure day comparison
  const createdDay = new Date(
    created.getFullYear(),
    created.getMonth(),
    created.getDate()
  );
  const todayDay = new Date(
    now.getFullYear(),
    now.getMonth(),
    now.getDate()
  );

  // How many full calendar days have passed since the listing was created?
  const daysDiff = Math.round(
    (todayDay.getTime() - createdDay.getTime()) / (24 * 60 * 60 * 1000)
  );

  // More than 1 day old → no badge
  if (daysDiff > 1 || daysDiff < 0) return null;

  // Created today: check for "This Hour" (within 4 hours of creation)
  if (daysDiff === 0) {
    const FOUR_HOURS_MS = 4 * 60 * 60 * 1000;
    if (now.getTime() - created.getTime() <= FOUR_HOURS_MS) {
      return "this-hour";
    }
  }

  // Created today (> 4 h ago) OR created yesterday → "New Today"
  return "new-today";
}
